import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommandCenterService } from 'src/app/services/command-center/command-center.service';

@Component({
	selector: 'app-command-center',
	templateUrl: './command-center.component.html',
	styleUrls: ['./command-center.component.scss'],
})
export class CommandCenterComponent implements OnInit {
	weekRevenue: any = null;
	leadsProduced: any = null;
	billableRate: any = null;
	leadsRemaining: any = null;
	productionBoard: any[] = [];
	loading = false;

	currentWeek: number;
	currentYear: number;
	weekLabel = '';

	constructor(private commandCenterSrv: CommandCenterService) {}

	ngOnInit(): void {
		const today = new Date();
		this.currentWeek = this.getISOWeek(today);
		this.currentYear = today.getFullYear();
		this.loadData();
	}

	get totalLeadsOrdered(): number {
		return this.productionBoard.reduce((sum, row) => sum + (row.ordered || 0), 0);
	}

	// ── Portable computed KPI sublines / banner (client-side, from fetched KPIs + board) ──
	get projectedRevenue(): number {
		return this.productionBoard.reduce((s, r) => s + (Number(r.leads_ordered) || 0) * (Number(r.lead_cost) || 0), 0);
	}
	/** Mon–Fri remaining in the displayed week, counting from max(today, week Monday); min 1. */
	get businessDaysLeft(): number {
		const monday = this.getISOWeekMonday(this.currentWeek, this.currentYear);
		const friday = new Date(monday);
		friday.setDate(monday.getDate() + 4);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const start = today < monday ? new Date(monday) : today;
		let count = 0;
		for (const d = new Date(start); d <= friday; d.setDate(d.getDate() + 1)) {
			if (d.getDay() >= 1 && d.getDay() <= 5) count++;
		}
		return Math.max(count, 1);
	}
	get perDayNeeded(): number {
		return Math.ceil((Number(this.leadsRemaining) || 0) / this.businessDaysLeft);
	}
	/** Account with the most leads remaining (banner "top priority"). */
	get topAccount(): any {
		return this.productionBoard.filter((r) => (Number(r.leads_remaining) || 0) > 0).sort((a, b) => (Number(b.leads_remaining) || 0) - (Number(a.leads_remaining) || 0))[0] || null;
	}
	topAccountValue(): number {
		const t = this.topAccount;
		return t ? (Number(t.leads_remaining) || 0) * (Number(t.lead_cost) || 0) : 0;
	}

	// Map completion_pct → prototype status labels (Unfilled→Urgent, In Progress, Complete→On track).
	statusLabel(row: any): string {
		const l = this.getStatusLabel(row.completion_pct, row.leads_remaining);
		return l === 'Complete' ? 'On track' : l === 'Unfilled' ? 'Urgent' : l;
	}
	statusPill(row: any): string {
		const l = this.statusLabel(row);
		return l === 'Urgent' ? 'cc-pill-urgent' : l === 'On track' ? 'cc-pill-ontrack' : 'cc-pill-progress';
	}

	get weekId(): string {
		return `${this.currentWeek}-${this.currentYear}`;
	}

	loadData(): void {
		this.loading = true;
		this.weekLabel = this.buildWeekLabel();

		forkJoin({
			weekRevenue: this.commandCenterSrv.getWeekRevenue(this.weekId).pipe(catchError(() => of(null))),
			leadsProduced: this.commandCenterSrv.getLeadsProduced(this.weekId).pipe(catchError(() => of(null))),
			billableRate: this.commandCenterSrv.getBillableRate(this.weekId).pipe(catchError(() => of(null))),
			leadsRemaining: this.commandCenterSrv.getLeadsRemaining(this.weekId).pipe(catchError(() => of(null))),
			productionBoard: this.commandCenterSrv.getPriorityProductionBoard(this.weekId).pipe(catchError(() => of(null))),
		}).subscribe({
			next: (results) => {
				// console.log('Command center data loaded', results);
				this.weekRevenue = results.weekRevenue?.body?.week_revenue ?? results.weekRevenue;
				this.leadsProduced = results.leadsProduced?.body?.leads_produced ?? results.leadsProduced;
				this.billableRate = results.billableRate?.body?.billable_rate ?? results.billableRate;
				this.leadsRemaining = results.leadsRemaining?.body?.leads_remaining ?? results.leadsRemaining;
				const board = results.productionBoard?.body ?? results.productionBoard;
				this.productionBoard = Array.isArray(board) ? board : [];
				this.loading = false;

				// console.log(this.weekRevenue, this.leadsProduced, this.billableRate, this.leadsRemaining, this.productionBoard);
			},
			error: (err) => {
				console.error('Error loading command center data', err);
				this.loading = false;
			},
		});
	}

	prevWeek(): void {
		if (this.currentWeek === 1) {
			this.currentYear--;
			this.currentWeek = this.getWeeksInYear(this.currentYear);
		} else {
			this.currentWeek--;
		}
		this.loadData();
	}

	nextWeek(): void {
		const maxWeeks = this.getWeeksInYear(this.currentYear);
		if (this.currentWeek >= maxWeeks) {
			this.currentWeek = 1;
			this.currentYear++;
		} else {
			this.currentWeek++;
		}
		this.loadData();
	}

	formatCurrency(value: any): string {
		if (value === null || value === undefined) return '—';
		const num = parseFloat(value);
		if (isNaN(num)) return '—';
		if (num >= 1000) {
			return `$${(num / 1000).toFixed(1)}k`;
		}
		return `$${num}`;
	}

	formatPercent(value: any): string {
		if (value === null || value === undefined) return '—';
		const num = parseFloat(value);
		if (isNaN(num)) return '—';
		return `${num.toFixed(1)}%`;
	}

	getStatusClass(completion_pct: any): string {
		const pct = parseFloat(completion_pct) || 0;
		if (pct >= 100) return 'bg-success';
		if (pct > 0) return 'bg-primary';
		return 'bg-warning text-dark';
	}

	getStatusLabel(completion_pct: any, leads_remaining: number): string {
		const pct = parseFloat(completion_pct) || 0;
		if (pct >= 100) return 'Complete';
		if (pct > 0) return 'In Progress';
		return leads_remaining > 0 ? 'Unfilled' : 'Complete';
	}

	private buildWeekLabel(): string {
		const monday = this.getISOWeekMonday(this.currentWeek, this.currentYear);
		return `Week of ${monday.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
	}

	private getISOWeek(date: Date): number {
		const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
		const dayNum = d.getUTCDay() || 7;
		d.setUTCDate(d.getUTCDate() + 4 - dayNum);
		const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
		return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
	}

	private getWeeksInYear(year: number): number {
		return this.getISOWeek(new Date(year, 11, 28));
	}

	private getISOWeekMonday(week: number, year: number): Date {
		const jan4 = new Date(year, 0, 4);
		const dow = jan4.getDay() || 7;
		const week1Monday = new Date(jan4);
		week1Monday.setDate(jan4.getDate() - (dow - 1));
		const monday = new Date(week1Monday);
		monday.setDate(week1Monday.getDate() + (week - 1) * 7);
		return monday;
	}

	getHealthClass(health: string): string {
		const map: Record<string, string> = {
			Healthy: 'cc-health-healthy',
			Watch: 'cc-health-watch',
			'At risk': 'cc-health-at-risk',
		};
		return map[health] || 'cc-health-healthy';
	}
}
